// In-game HUD strings (Spanish) — must provide every key of gameEn
// (checked by its type). Split out of game.ts alongside gameEn.ts.
// The help/settings half lives in ./gameHelpEs.ts.

import type { GameCoreKey } from './gameEn.js';

export const gameEs: Record<GameCoreKey, string> = {
  // Shared
  'common.close': 'Cerrar',
  'common.cancel': 'Cancelar',
  'common.off': 'No',
  'common.unknown': 'desconocida',
  'common.player': 'Jugador {player}',
  'city.neutral': 'Aldea neutral',
  'common.playerShort': 'J{player}',
  'common.playerWins': 'Gana el jugador {player}',
  'common.hp': 'PV',
  'common.hpLower': 'pv',

  // Event log + event feed
  'eventLog.title': 'Eventos',
  'eventLog.label': 'Registro de eventos',

  'event.doctrineInvested': 'Doctrina invertida',
  'event.unitMoved': 'Unidad movida',
  'event.unitCombat': 'Combate',
  'event.unitGarrisoned': 'Unidad acuartelada',
  'event.unitDeployed': 'Unidad desplegada',
  'event.cityAttacked': 'Ciudad atacada',
  'event.cityDestroyed': 'Ciudad destruida',
  'event.cityDamaged': 'Ciudad dañada',
  'event.buildingStarted': 'Construcción iniciada',
  'event.buildingCompleted': 'Edificio terminado',
  'event.heroAbilityActivated': 'Habilidad de héroe usada',
  'event.heroAbilitySelected': 'Habilidad de héroe aprendida',
  'event.trigger': 'Evento',
  'event.merchantDocked': 'Una casa en tu puerto',
  'event.merchantRoute': 'Se abre una ruta comercial',
  'event.merchantAccepted': 'Aceptas la oferta',
  'event.merchantDeclined': 'Rechazas la oferta',
  'event.detail.doctrineInvested': 'Jugador {player} → {archetype}',
  'event.detail.unitMoved': 'Unidad de ({from}) a ({to})',
  'event.detail.unitCombat': 'contra el defensor en ({to})',
  'event.detail.unitGarrisoned': 'Acuartelada en la ciudad ({coord})',
  'event.detail.unitDeployed': 'Desplegada desde la ciudad ({coord})',
  'event.detail.damage': 'Daño: {damage}',
  'event.detail.cityRazed': 'Ciudad arrasada',
  'event.detail.merchantDocked': 'La {house} ofrece {gold} de oro y {grain} de grano por ticada.',
  'event.detail.merchantRoute': 'La {house} paga {gold} de oro y {grain} de grano por ticada.',
  'event.detail.merchantAccepted': 'La {house} comercia contigo.',
  'event.detail.abilityActivated': 'Casilla {slot}: {ability}',
  'event.detail.abilitySelected': 'Aprendida: {ability}',

  // Victory paths
  'victory.path.conquest': 'Conquista',
  'victory.path.economy': 'Economía',
  'victory.path.science': 'Ciencia',
  'victory.path.faith': 'Fe',
  'victory.path.diplomacy': 'Diplomacia',
  'victory.progress': 'Progreso hacia la victoria',
  'victory.title.conquest': 'Victoria por conquista',
  'victory.title.economy': 'Victoria económica',
  'victory.title.science': 'Victoria científica',
  'victory.title.faith': 'Victoria por la fe',
  'victory.title.diplomacy': 'Victoria diplomática',
  'victory.title.milestones': 'Victoria por hitos',
  'victory.desc.conquest': 'Todas las ciudades del mapa son tuyas.',
  'victory.desc.economy': 'Tu tesoro y tu red comercial dominan el reino.',
  'victory.desc.science': 'El saber que has acumulado te ha abierto la comprensión absoluta.',
  'victory.desc.faith': 'Tu fe y tus templos han unido al mundo bajo tu credo.',
  'victory.desc.diplomacy': 'Tu honor y tus alianzas han asegurado una paz duradera.',
  'victory.desc.milestones': 'Has completado los grandes hitos del reino antes que nadie.',
  'victory.youWin': '¡Has ganado!',
  // Graduation suggestion (rulesets spec §10 / R8).
  'victory.graduation.mastered': '¡Has dominado {preset}!',
  'victory.graduation.adds': 'Añade {list}.',
  'victory.graduation.tryNext': 'Probar {next}',
  'victory.paused':
    'La simulación está en pausa; empieza una nueva partida o cierra este aviso para examinar el tablero final.',
  'victory.dismiss': 'Cerrar el aviso de victoria',
  'victory.viewSummary': 'Ver resumen',
  'victory.viewSummaryLabel': 'Ver el resumen de la partida',
  'victory.menu': 'Menú',
  'victory.newMatchLabel': 'Empezar una nueva partida',
  'victory.matchResult': 'Resultado de la partida',

  // Post-victory summary
  'summary.title': 'Resumen de la partida',
  'summary.you': '(Tú)',
  'summary.winner': 'Ganador',
  'summary.cities': 'Ciudades',
  'summary.units': 'Unidades',
  'summary.doctrines': 'Doctrinas',
  'summary.gold': 'Oro',
  'summary.industry': 'Industria',
  'summary.knowledge': 'Conocimiento',
  'summary.returnToMenu': 'Volver al menú',
  'summary.playAgain': 'Jugar de nuevo',

  // Unit selection tray
  'unitTray.label': 'Unidades seleccionadas',
  'unitTray.hero': 'Héroe',
  'unitTray.level': 'Nv{level}',
  'unitTray.mp': 'PM',
  'unitTray.emptySlot': 'casilla vacía',

  // Right panel
  'rightPanel.info': 'INFORMACIÓN',
  'rightPanel.label': 'Información de la partida',
  'rightPanel.matchOverview': 'Resumen de la partida',
  'rightPanel.overview': 'Resumen',
  'rightPanel.diplomacy': 'Diplomacia',
  'rightPanel.viewDetails': 'Ver detalles',
  'rightPanel.closeSelection': 'Cerrar selección',
  'rightPanel.closeInfo': 'Cerrar el panel de información',
  'selection.tile': 'Casilla',
  'selection.city': 'Ciudad',
  'selection.unit': 'Unidad',
  'selection.units': 'Unidades ({count})',
  'overview.turnYear': 'Turno {turn} · Año {year}',
  'overview.victoryProgress': 'PROGRESO HACIA LA VICTORIA',
  'overview.tracking': 'Calculando…',
  'overview.wonder': 'Maravilla',
  'overview.domination': 'Dominación',
  'overview.doctrine': 'DOCTRINA',
  'overview.doctrineEmpty': 'Las doctrinas estarán disponibles cuando se cargue un jugador.',
  'overview.recentEvents': 'EVENTOS RECIENTES',
  'overview.noEvents': 'Aún no hay eventos: sal a explorar.',
  'diplomacy.empty':
    'Aún no has descubierto a otros jugadores. Encuentra otra civilización para ver aquí vuestras relaciones.',
  'diplomacy.knownPlayers': 'Jugadores conocidos ({count})',
  'diplomacy.neutral': 'neutral',

  // Doctrine rail
  'doctrine.label': 'Selección de doctrina',
  'doctrine.tab.doctrine': 'Doctrina',
  'doctrine.tab.artifacts': 'Artefactos',
  'doctrine.title': 'DOCTRINAS',
  'doctrine.points.one': '{count} punto disponible',
  'doctrine.points.other': '{count} puntos disponibles',
  'doctrine.nextGrant': ' · el siguiente en {ticks} t',
  'doctrine.noPlayer': 'ningún jugador cargado',
  'doctrine.unitAttack': '+{bonus} de ataque de unidades (Industria)',
  'doctrine.hybridHint': 'invierte suficientes puntos en dos arquetipos para desbloquear {forms}',
  'doctrine.hybridForms': 'formas híbridas',
  'doctrine.next': 'Siguiente: ',
  'doctrine.atCap': 'nivel máximo alcanzado',
  'doctrine.noPoints': 'sin puntos disponibles',
  'doctrine.invest': 'invertir',
  'doctrine.investLabel': 'Invertir un punto de doctrina en {name}',
  'doctrine.unspecialized': 'Sin especializar',
  'doctrine.artifactBonus': 'Bonificación de artefacto activa',
  'doctrine.artifactBonusLabel': 'bonificación de artefacto',

  'archetype.industry': 'Industria',
  'archetype.industry.blurb': 'Orden mediante la creación.',
  'archetype.industry.tier1': 'Gremios de herreros y artesanos',
  'archetype.industry.tier2': 'Forjas y gremios mineros',
  'archetype.industry.tier3': 'Industria de masas y cuerpos logísticos',
  'archetype.industry.tier4': 'Ciudades fábrica y pactos steampunk',
  'archetype.industry.tier5': 'Proyectos de megaestructuras',
  'archetype.science': 'Ciencia',
  'archetype.science.blurb': 'Verdad mediante el conocimiento.',
  'archetype.science.tier1': 'Erudición, rueda y calendario',
  'archetype.science.tier2': 'Universidades y óptica',
  'archetype.science.tier3': 'Imprenta y astronomía',
  'archetype.science.tier4': 'Computación y cohetería',
  'archetype.science.tier5': 'Mentes ascendidas y singularidades',
  'archetype.faith': 'Fe',
  'archetype.faith.blurb': 'Fuerza mediante la creencia.',
  'archetype.faith.tier1': 'Santuarios y sacerdotes tribales',
  'archetype.faith.tier2': 'Templos y órdenes sagradas',
  'archetype.faith.tier3': 'Misioneros y teocracia',
  'archetype.faith.tier4': 'Megaiglesias y milagros',
  'archetype.faith.tier5': 'Apoteosis y relicarios santos',
  'archetype.arcane': 'Arcano',
  'archetype.arcane.blurb': 'Poder mediante el misterio.',
  'archetype.arcane.tier1': 'Hechiceros rurales y tocados por las líneas ley',
  'archetype.arcane.tier2': 'Torres de magos y vínculos con familiares',
  'archetype.arcane.tier3': 'Conductos planares y ataduras',
  'archetype.arcane.tier4': 'Tormentas de hechizos y pactos con dragones',
  'archetype.arcane.tier5': 'Archimago divino y soberano planar',
  'archetype.nature': 'Naturaleza',
  'archetype.nature.blurb': 'Armonía mediante la vida.',
  'archetype.nature.tier1': 'Círculos druídicos y domadores de bestias',
  'archetype.nature.tier2': 'Centinelas y caminantes de las líneas ley',
  'archetype.nature.tier3': 'Señores ents y raíces del mundo',
  'archetype.nature.tier4': 'Vínculo leviatán y moldeado del mundo',
  'archetype.nature.tier5': 'Avatar de Gaia y fusión con la biosfera',
  'archetype.chaos': 'Caos',
  'archetype.chaos.blurb': 'Libertad mediante el desorden.',
  'archetype.chaos.tier1': 'Bandas de saqueadores y sacrificio de sangre',
  'archetype.chaos.tier2': 'Células de sabotaje y siembra de plagas',
  'archetype.chaos.tier3': 'Grietas disformes y pactos demoníacos',
  'archetype.chaos.tier4': 'Cultos del apocalipsis y distorsiones de la realidad',
  'archetype.chaos.tier5': 'Caos encarnado e invocación de falsos dioses',

  'artifacts.title': 'ARTEFACTOS',
  'artifacts.collected': '{count} recogidos',
  'artifacts.empty': 'Aún no tienes artefactos. Explora ruinas con una unidad exploradora.',
  'artifacts.installed': 'Instalados',
  'artifacts.install': 'Instalar en una ciudad',
  'artifacts.installedIn': 'Instalado en {city}',

  // Biomes
  'biome.plains': 'Llanuras',
  'biome.forest': 'Bosque',
  'biome.hills': 'Colinas',
  'biome.mountain': 'Montaña',
  'biome.desert': 'Desierto',
  'biome.tundra': 'Tundra',
  'biome.coast': 'Costa',
  'biome.ocean': 'Océano',
  'biome.unknown': 'Desconocido',

  // Tile / city / unit info
  'tile.traversable': 'transitable',
  'tile.impassable': 'infranqueable',
  'tile.traversableHint': 'Las unidades terrestres pueden cruzar esta casilla.',
  'tile.impassableHint': 'Bloquea el movimiento terrestre.',
  'tile.biome': 'bioma',
  'tile.resource': 'recurso',
  'tile.axial': 'axial',
  'tile.variant': 'variante',

  'city.tier1': 'Asentamiento',
  'city.tier2': 'Pueblo',
  'city.tier3': 'Ciudad',
  'city.tier4': 'Metrópolis',
  'city.tier5': 'Megaciudad',
  'city.tierN': 'Nivel {tier}',
  'city.tierBadge': '{name} (N{tier})',
  'city.health': 'salud de la ciudad',
  'city.culture': 'cultura',
  'culture.imperial': 'Imperial',
  'culture.imperial.blurb': 'Mármol blanco, tejados de terracota, columnas y cúpulas.',
  'culture.nordic': 'Nórdica',
  'culture.nordic.blurb': 'Salones de madera oscura, frontones empinados, mascarones de dragón.',
  'culture.eastern': 'Oriental',
  'culture.eastern.blurb': 'Pilares bermellón, tejados escalonados curvos, pagodas.',
  'city.population': 'población',
  'city.influence': 'influencia',
  'city.influenceValue': '{radius} hex.',
  'city.garrison': 'guarnición',
  'city.garrisonUnits.one': '{count} unidad',
  'city.garrisonUnits.other': '{count} unidades',
  'city.attack': 'Atacar la ciudad',
  'city.deploy': 'Desplegar la guarnición ({count})',
  'city.doctrine': 'Doctrina de la ciudad',
  'city.doctrine.needsArtifact': 'Fuera de la senda: requiere un artefacto instalado',
  'city.doctrine.needsArtifactHint':
    'Las doctrinas fuera de la senda requieren un artefacto instalado en la ciudad.',

  'unit.health': 'salud de la unidad',
  'unit.movement': 'movimiento',
  'unit.cargo': 'Carga',
  'unit.sailing': 'Ruta',
  'unit.shipHint': 'Haz clic en un puerto para cargar o vender.',
  'ship.shipyard': 'Astillero',
  'ship.trade': 'Barco mercante',
  'ship.transport': 'Transporte',
  'ship.warship': 'Nave de guerra',
  'ship.galleon': 'Galeón',
  'ship.cost': 'Coste',
  'ship.turns': '{turns} turnos',
  'ship.build': 'Construir',
  'ship.locked': 'Falta astillero',
  'sea.offerTitle': 'Una casa está en tu puerto',
  'sea.offerTerms':
    'La {house} ofrece {gold} de oro y {grain} de grano por ticada, y {gift} de oro para cerrar el trato.',
  'sea.accept': 'Aceptar',
  'sea.decline': 'Rechazar',
  'sea.house1': 'Casa de la Sal',
  'sea.house2': 'Casa del Ámbar',
  'sea.house3': 'Casa del Hierro',
  'sea.merchantVessel': 'Buque mercante',
  'sea.standingTitle': 'Tu postura con las casas',
  'sea.standingCold': 'Las casas te han vuelto frío: llaman menos y pagan menos.',
  'sea.standingShunned': 'Las casas no te están llamando ahora mismo.',
  'sea.routesTitle': 'Casas extranjeras',
  'sea.route': '{gold} de oro, {grain} de grano por ticada',
  'sea.routesBlocked': 'Bloqueado — estas casas no pagan nada hasta que los corsarios se marchen.',
  'unit.water': 'Agua',
  'unit.waterLevel': 'reserva de agua de la unidad',
  'unit.formation': 'Formación',
  'unit.regular': 'Unidad normal: sin habilidades de héroe.',
  'formation.standard': 'Estándar',
  'formation.line': 'Línea',
  'formation.column': 'Columna',
  'formation.wedge': 'Cuña',
  'formation.hold': 'Posición',
  'formation.phalanx': 'Falange',
  'formation.skirmish': 'Escaramuza',

  'building.title': 'Edificios',
  'building.inProgress': 'Construyendo {name}... ({pct} %)',
  'building.progress': 'progreso de la construcción',
  'building.queue': 'Cola ({count})',
  'building.recruiting': 'Reclutando {id}',
  'building.building': 'Construyendo {id}',
  'building.itemProgress': 'progreso de {id}',
  'building.requiresTier': 'Requiere ciudad de nivel {tier}',
  'building.requires': 'Requiere {building}',
  'building.alreadyQueued': 'Ya en construcción o en cola aquí',
  'building.alreadyBuilt': 'Ya construido',
  'building.tooExpensive': 'Demasiado caro',
  'building.build': 'Construir {name}',
  'building.cost': '{cost} ind.',

  // suggest auto-manage mode (play-modes spec §8)
  'suggest.title': 'El ayudante sugiere',
  'suggest.train': 'Entrenar {name}',

  'preview.title': 'PREVISIÓN DE ATAQUE',
  'preview.vsNearby': '{kind} contra enemigos cercanos',
  'preview.favorable': 'Favorable',
  'preview.even': 'Igualado',
  'preview.unfavorable': 'Desfavorable',
  'preview.terrain': '+{bonus} terreno',
  'preview.terrainTitle': 'Bonificación de terreno del defensor: +{bonus}',
  'preview.classCounter': 'Ventaja de clase',
  'preview.formationCounter': 'Ventaja de formación',
  'preview.total': 'Multiplicador total',

  // Heroes
  'hero.unknown': 'Héroe desconocido',
  'hero.level': 'Nivel',
  'hero.xp': 'PX',
  'hero.wounded': 'Herido',
  'hero.captured': 'Capturado',
  'hero.attributes': 'ATRIBUTOS',
  'hero.attr.martial': 'MAR',
  'hero.attr.intellect': 'INT',
  'hero.attr.charisma': 'CAR',
  'hero.attr.cunning': 'AST',
  'hero.attr.faith': 'FE',
  'hero.loyalty': 'Lealtad',
  'hero.traits': 'RASGOS',
  'hero.aura': 'AURA ACTIVA',
  'hero.auraStats': 'Radio {radius} · ATQ x{attack} · DEF x{defense}',
  'hero.abilities': 'HABILIDADES',
  'hero.emptySlot': 'Casilla {slot}: vacía',
  'hero.cooldown': 'Recarga: {ticks} t',
  'hero.use': 'Usar',
  'hero.picks.one': '{count} habilidad por elegir',
  'hero.picks.other': '{count} habilidades por elegir',
  'hero.noAbilities': 'No hay habilidades disponibles en este nivel.',
  'hero.known': '(conocida)',

  // Entity detail modal
  'entity.entity': 'Entidad',
  'entity.population': 'Población: {count}',
  'entity.tier': 'Nivel: {tier}',
  'entity.biome': 'Bioma: {biome}',
  'entity.resource': 'Recurso: {resource}',
  'entity.attack': 'Atacar',
  'entity.deploy': 'Desplegar',
  'entity.heroLevel': '{archetype} · Nivel {level}',
  'entity.role': 'Rol: {role}',
  'entity.xp': 'PX: {xp}',
  'entity.loyalty': 'Lealtad: {loyalty}',
  'entity.viewDetails': 'Ver detalles',

  // Combat overlay + alerts
  'combat.title': 'Combate',
  'combat.titleCount': 'Combate: {count} activos',
  'combat.active': '{count} activos',
  'combat.tickerLabel.one': 'Resumen de combates: {count} combate activo',
  'combat.tickerLabel.other': 'Resumen de combates: {count} combates activos',
  'combat.vs': '{attacker} contra {defender}',
  'combat.moreNearby': '+{count} más cerca',
  'combat.distant.one': 'Pacto Continental: {count} asentamiento perdido',
  'combat.distant.other': 'Pacto Continental: {count} asentamientos perdidos',
  'combat.region.capital': 'capital',
  'combat.region.north': 'norte',
  'combat.region.south': 'sur',
  'combat.region.east': 'este',
  'combat.region.west': 'oeste',
  'combat.engagement': 'Enfrentamiento',
  'combat.minimize': 'Minimizar',
  'combat.noRounds': 'Aún no hay asaltos. Esperando el primer golpe.',
  'combat.strike': '{attacker} golpeó a {defender} con {damage} de daño',
  'combat.morale': 'moral de {unit}: {level}',
  'combat.morale.high': 'alta',
  'combat.morale.medium': 'media',
  'combat.morale.low': 'baja',
  'combat.morale.critical': 'crítica',
  'combat.kill': ' · BAJA',
  'toast.label': 'Alertas de combate',
  'toast.attacked': '¡Han atacado tu ciudad!',
  'toast.damage': '−{damage} PV',
  'toast.remaining': '(quedan {hp})',
  'toast.dismiss': 'Descartar la alerta de ataque',
  'toast.expand': 'Mostrar detalles del ataque',
  'toast.collapse': 'Ocultar detalles del ataque',
};
